import { useState } from "react";
import type {
  AddQuoteResult,
  PolicyHolder,
  Quote,
  SiteContent,
  WebShopPublicUser,
} from "@/api_client";
import { useContent } from "@/hooks/api/content/useContent";
import { useOffer } from "@/hooks/api/offer/useOffer";
import { useUser } from "@/hooks/api/useUser";
import { useSubmitEntryHandler } from "@/hooks/product_handlers/useSubmitEntryHandler";
import { getProductNavigationHook } from "./useNoopProductNavigationHandlers";

type UseEntryParams<TQuote extends Quote, TContent = undefined> = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: TContent | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: TQuote) => Promise<AddQuoteResult>;
  contentKey?: keyof SiteContent;
  fetchSiteContent?: boolean;
  disableUserAndPolicyHolderLookup?: boolean;
};

type UseEntryResult<TQuote extends Quote, TContent = undefined> = {
  currentUser?: WebShopPublicUser;
  currentPolicyHolder: PolicyHolder | null;
  content?: TContent | null;
  siteContent?: SiteContent;
  isLoading: boolean;
  submitHandler: (quote: TQuote) => Promise<unknown>;
  requiresMoreInformation: boolean;
};

// Entries are defined as part of the next application to make development easier.
// They are also built out as web components.
// This means we use them in 2 different runtime environments.
// When we use them as web components, we will pass in data as well as submit handlers these props are called external***.
// When externals are passed we use those instead of doing lookups or fetching data ourselves.
export const useEntry = <TQuote extends Quote, TContent = undefined>({
  externalUser,
  externalPolicyHolder,
  externalContent,
  externalIsLoading,
  externalSubmitHandler,
  contentKey,
  fetchSiteContent,
  disableUserAndPolicyHolderLookup,
}: UseEntryParams<TQuote, TContent>): UseEntryResult<TQuote, TContent> => {
  const shouldLookupUserAndPolicyHolder = !disableUserAndPolicyHolderLookup;
  const { data: user, isLoading: isUserLoading } = useUser(
    shouldLookupUserAndPolicyHolder && !externalUser,
  );
  const { data: offer, isLoading: isOfferLoading } = useOffer(
    shouldLookupUserAndPolicyHolder && !externalPolicyHolder,
  );
  const [hasExternalSubmitHandler] = useState(() =>
    Boolean(externalSubmitHandler),
  );

  const navigationHook = getProductNavigationHook(!hasExternalSubmitHandler);
  const { submitEntryHandler, isPending, success, requiresMoreInformation } =
    useSubmitEntryHandler<TQuote>(navigationHook);

  const shouldFetchContentByDefault =
    Boolean(contentKey) && externalContent == null;
  const { data: siteContent } = useContent(
    fetchSiteContent ?? shouldFetchContentByDefault,
  );

  const currentUser = externalUser ?? user;
  const currentPolicyHolder =
    externalPolicyHolder ??
    (shouldLookupUserAndPolicyHolder ? (offer?.policyHolder ?? null) : null);
  const content =
    externalContent ??
    (contentKey
      ? ((siteContent?.[contentKey] as TContent | null | undefined) ?? null)
      : null);

  const isExternalSubmit = Boolean(externalSubmitHandler);
  const isLoading = isExternalSubmit
    ? Boolean(externalIsLoading)
    : Boolean(externalIsLoading) ||
      isPending ||
      success ||
      isUserLoading ||
      isOfferLoading;
  const submitHandler = externalSubmitHandler ?? submitEntryHandler;

  return {
    currentUser,
    currentPolicyHolder,
    content,
    siteContent,
    isLoading,
    submitHandler,
    requiresMoreInformation,
  };
};
